/**
 * License tier identity — this app's single source of truth for a license's
 * display name, color family, and icon. The admin app carries the same
 * slug-to-name pairs in its own `utils/license.ts`, because the two apps share
 * no package that a license catalog belongs in; a tier renamed in one is
 * renamed in the other. Business surfaces consume it two ways: the
 * shared `LicenseBadge` component (employee roster rows, employee drawer,
 * financial license blocks, partner provisioning, partner analytics
 * breakdowns) renders the badge straight from this map, and label-only
 * surfaces (the roster license-availability cards and the create-employee
 * review summary) resolve the name through `licenseTierName` / `licenseTier`.
 *
 * Keyed on the license slug, which is the catalog's identity for a tier and the
 * value every API payload carries:
 *   - Business catalog: essentials_business / pro_business / signature_business
 *   - Consumer catalog: essentials / pro / signature (LicenseType)
 *
 * Same `hue` = same tier rank across the business and consumer catalogs
 * (violet=Signature > blue=Pro > teal=Essentials > slate=entry). The hue names the
 * rank; `LicenseBadge` paints it from the design system's own tier tokens. The trailing
 * "Business" on the sold business names keeps the two catalogs unambiguous.
 */

export type LicenseTierHue = 'violet' | 'blue' | 'teal' | 'slate';

export interface LicenseTierIdentity {
    /** Human-facing tier name. */
    name: string;
    /** Color family conveying tier rank. */
    hue: LicenseTierHue;
    /** Icon asset filename under /images/business. */
    icon: string;
    /** Ascending premium order — the single source for sorting tier lists. */
    rank: number;
}

/**
 * A license as it arrives from the API: a slug string, or an object carrying the
 * slug and, on the payloads that include it, the catalogue's own display name. A
 * field the payload sends as null, such as a consolidation block's slug, reads as
 * absent.
 */
export type LicenseInput =
    | string
    | { type?: string | null; slug?: string | null; name?: string | null; display?: string | null }
    | null
    | undefined;

export const LICENSE_TIERS: Record<string, LicenseTierIdentity> = {
    // Business catalog
    signature_business: { name: 'Signature Business', hue: 'violet', icon: 'tier-upper.svg', rank: 3 },
    pro_business: { name: 'Pro Business', hue: 'blue', icon: 'tier-upper.svg', rank: 2 },
    essentials_business: { name: 'Essentials Business', hue: 'teal', icon: 'tier-entry.svg', rank: 1 },
    // Consumer catalog
    signature: { name: 'Signature', hue: 'violet', icon: 'tier-upper.svg', rank: 3 },
    pro: { name: 'Pro', hue: 'blue', icon: 'tier-upper.svg', rank: 2 },
    essentials: { name: 'Essentials', hue: 'teal', icon: 'tier-entry.svg', rank: 1 },
};

/**
 * The value this app sends for "no seat", to take one away or to add an employee without
 * one. The API reads it as no seat on both writes; null would do on the update, but on the
 * create null means Auto-Provision. It never comes back in a payload, where no seat is null.
 */
export const NO_LICENSE_SLUG = 'none';

const NO_LICENSE: LicenseTierIdentity = { name: 'No license', hue: 'slate', icon: 'no_license.svg', rank: -1 };

/**
 * Normalize a raw license value to its slug.
 *
 * `slug` wins over `type` because only `slug` is always a slug: the billing
 * payload carries the display name under `type` and the slug beside it, while
 * the employee payload carries the slug under `type` and no `slug` at all. Read
 * the other way round, a billing license would key this map on its name and
 * miss every entry.
 */
export function licenseSlug(license: LicenseInput): string {
    if (!license) return '';
    if (typeof license === 'string') return license;
    return license.slug || license.type || '';
}

/**
 * The catalogue name an API payload carries for a license, when it carries one.
 *
 * A partner owns its own SKUs, so their names live in the catalogue row and
 * nowhere in this map. Which field carries that name depends on the payload:
 * `display` on the person payload, `name` on the admin one, and `type` on the
 * billing one, where the slug travels separately under `slug`. `type` is the
 * slug itself on the employee payload, so a candidate equal to the slug is
 * discarded and the slug is made readable instead.
 */
function catalogName(license: LicenseInput, slug: string): string | null {
    if (!license || typeof license === 'string') return null;
    const named = license.display || license.name || license.type;
    return named && named !== slug ? named : null;
}

/**
 * Turn a slug into something a person can read, for the last resort where a
 * license reaches the UI with no name attached.
 */
function humanize(slug: string): string {
    return slug
        .split(/[_-]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Resolve a license to its tier identity. `none`, or nothing at all → No license. A slug
 * this map does not carry takes a neutral slate badge and the name the payload carries,
 * or a readable form of the slug when it carries none. That covers every partner-owned
 * SKU, and every retired tier a historical seat or invoice still names. A raw slug is
 * never shown to a person.
 *
 * A payload with no slug but a catalogue name is a license, and the name is the only
 * evidence of which one: the consolidation preview sends blocks that way, so the name
 * wins over the empty slug. `none` is different — it is this app's own word for no seat,
 * never a catalogue slug — so it still wins.
 */
export function licenseTier(license: LicenseInput): LicenseTierIdentity {
    const slug = licenseSlug(license);
    if (slug === NO_LICENSE_SLUG) return NO_LICENSE;

    const known = slug ? LICENSE_TIERS[slug] : undefined;
    if (known) return known;

    const named = catalogName(license, slug);
    if (!slug && !named) return NO_LICENSE;

    return { name: named ?? humanize(slug), hue: 'slate', icon: 'scan.svg', rank: 0 };
}

/** Display name for a license, from the single source of truth. */
export function licenseTierName(license: LicenseInput): string {
    return licenseTier(license).name;
}

/** Ascending premium rank — sort tier lists by this instead of a hardcoded order. */
export function licenseTierRank(license: LicenseInput): number {
    return licenseTier(license).rank;
}

/**
 * The BUSINESS tier one step above a license, or undefined when there is nothing above it — and
 * undefined for no license at all, since there is then nothing to move up from.
 *
 * Read off `rank` like its siblings here, so a new tier joins the ladder on its own rather than in
 * a second hand-written order. Business-side only because the only caller is a company purchase: an
 * employer upgrading an employee buys from the company catalogue whichever catalogue the license on
 * screen came from.
 */
export function nextBusinessTierAbove(license: LicenseInput): string | undefined {
    const rank = licenseTierRank(license);
    if (rank < 0) return undefined;

    return Object.entries(LICENSE_TIERS)
        .filter(([slug, tier]) => slug.endsWith('_business') && tier.rank > rank)
        .sort(([, a], [, b]) => a.rank - b.rank)[0]?.[0];
}

/** The highest rank either catalog reaches, read off the map so a new top tier lands here on its own. */
const TOP_TIER_RANK = Math.max(...Object.values(LICENSE_TIERS).map((tier) => tier.rank));

/**
 * True when a license already sits at the top of its catalog, so there is
 * nothing to upgrade to. Read from `rank` rather than matched against a tier
 * name: a name comparison answers "no" for every business tier, which offers
 * the top business seat an upgrade that does not exist.
 */
export function isTopTierLicense(license: LicenseInput): boolean {
    return licenseTierRank(license) >= TOP_TIER_RANK;
}

